import { Spinner } from 'components/core';
import { findModelForEditing, submitServerChange } from 'components/dataCreationForms/common';
import {
  EditContext,
  OpenEditDialogParams,
} from 'components/dataCreationForms/types';
import {
  useLevelsToSelect,
} from 'store/level';
import { getSitesStoreState, useSites } from 'store/site';
import { siteOnViewToSiteEditModel } from 'shared/domain/site/mapping/toModel';
import { siteClient } from 'shared/domain/site/siteClient';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { siteModelToSiteOnView } from 'views/site/mapping';
import { SiteOnView } from 'shared/domain/site/types/view';
import { handleFormValidation, validate } from '../validation';
import { useProject } from 'store/project';

const FORM_NAME = 'site';
const SUBMIT_EVENT_TYPE = 'submit-site';
const WithEditSiteContext = createContext<
  EditContext<SiteOnView> | undefined
>(undefined);

function WithEditSite({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const {
    items: sites,
    loading: loadingSites,
  } = useSites();
  const { organizationId, _id: projectId } = useProject();

  const { levelsPerSite } = useLevelsToSelect();

  const [isPosting, setIsPosting] = useState<boolean>(false);
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const [open, setOpen] = useState(false);
  const [existingValues, setExistingValues] = useState<
    SiteOnView | undefined
  >(undefined);

  const openDialog = useCallback(
    ({ _id }: OpenEditDialogParams) => {
      const existingSiteModel = findModelForEditing(sites, _id);
      let siteOnView: SiteOnView | undefined;

      if (existingSiteModel) {
        siteOnView = siteModelToSiteOnView(
          existingSiteModel,
          levelsPerSite,
          organizationId
        );
        setExistingValues(siteOnView);
        setOpen(true);
      }
    },
    [sites, levelsPerSite, organizationId]
  );

  const onSuccess = useCallback(
    (_id, label) => onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );

  const confirmCancel = useCancelConfirmation(FORM_NAME);

  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: SiteOnView) => {
      const siteEditModel = siteOnViewToSiteEditModel(values, projectId);
      const { saved } = await submitServerChange({
        change: () => siteClient.edit(siteEditModel),
        setOpen,
        setIsPosting,
        action: 'updating a site',
      });
      if (saved) {
        void getSitesStoreState().actions.reload();
      }
    },
    [projectId]
  );

  const ctx: EditContext<SiteOnView> = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      existingValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate,
      handleFormValidation,
      onSuccess,
      formName: FORM_NAME,
    };
  }, [
    openConfirmationDialog,
    existingValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    onSuccess,
  ]);
  if (loadingSites) {
    return (
      <Spinner
        reason={`SiteWizard loadingSites: ${loadingSites} || existingValues: ${existingValues}`}
      />
    );
  }
  return (
    <WithEditSiteContext.Provider value={ctx}>
      {children}
    </WithEditSiteContext.Provider>
  );
}

function useEditSite(): EditContext<SiteOnView> {
  const context = useContext(WithEditSiteContext);
  if (context === undefined) {
    throw new Error('useEditSite must be used within an EditSiteContext');
  }
  return context;
}

const withEditSite =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithEditSite>
      <Component {...props} />
    </WithEditSite>
  );

export { WithEditSite, useEditSite, withEditSite };
